import { inject, Injectable, isDevMode, OnDestroy } from "@angular/core";
import { ApplicationInsights, SeverityLevel } from "@microsoft/applicationinsights-web";
import { Store } from "@ngxs/store";
import * as ApplicationStateActions from "@vp/data-access/application";
import { EventAggregator } from "@vp/data-access/application";
import { OrganizationFeatures } from "@vp/models";
import { FeatureService } from "@vp/shared/features";
import { APP_INSIGHTS_KEY } from "@vp/shared/tokens";
import { Observable, of, Subject, zip } from "rxjs";
import { catchError, filter, mergeMap, take, takeUntil, tap } from "rxjs/operators";
import { AppInsightsEvent } from "./app-insights-event";

@Injectable({
  providedIn: "root"
})
export class AppInsightsProxyService implements OnDestroy {
  private readonly featureService = inject(FeatureService);
  private readonly eventAggregator = inject(EventAggregator);
  private readonly store = inject(Store);

  private appInsights!: ApplicationInsights;
  private appInsightsKey: string | undefined;
  private initialized = false;
  private destroy$ = new Subject<void>();

  constructor() {
    const appInsightsKey = inject(APP_INSIGHTS_KEY);

    this.appInsightsKey = appInsightsKey;
  }

  init() {
    if (this.initialized || isDevMode()) return;
    this.initialized = true;

    this.featureService
      .featureFlagEnabled$(OrganizationFeatures.common, "applicationInsightsTelemetryEnabled")
      .pipe(filter(Boolean), take(1))
      .subscribe(() => {
        this.appInsights = new ApplicationInsights({
          config: {
            instrumentationKey: this.appInsightsKey,
            enableAutoRouteTracking: false, // option to log all route changes
            disablePageUnloadEvents: ["unload"]
          }
        });
        this.appInsights.loadAppInsights();
        this.listen();
      });
  }

  listen() {
    this.eventAggregator
      .on<AppInsightsEvent>(AppInsightsEvent)
      .pipe(
        mergeMap((e: AppInsightsEvent) => {
          let handled: Observable<boolean> = of(false);
          switch (e.args.type) {
            case "event":
              if (e.args.source) {
                handled = this.logEvent(e.args.source, e.args);
              }
              break;
            case "exception":
              handled = this.logException(e.args.error, e.sender, e.args);
              break;
            case "metric":
              if (e.args.source) {
                handled = this.logMetric(e.args.source, e.args.average, e.args);
              }
              break;
            case "pageView":
              handled = this.logPageView(e.args.source, "");
              break;
            case "trace":
              if (e.args.message) {
                handled = this.logTrace(e.args.message, e.args);
              }
              break;
            default:
              break;
          }
          return zip(handled, of(e));
        }),
        tap(([handled, event]) => {
          if (handled) {
            this.store.dispatch(new ApplicationStateActions.EventHandled(event));
          }
        }),

        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private logEvent(name: string, properties?: { [key: string]: any }) {
    return new Observable<boolean>(observer => {
      this.appInsights.trackEvent({ name }, properties);
      observer.next(true);
      observer.complete();
    }).pipe(catchError(() => of(false)));
  }

  private logException(error: Error, areaOfApp?: string, properties?: { [key: string]: any }) {
    return new Observable<boolean>(observer => {
      this.appInsights.trackException(
        {
          id: areaOfApp,
          exception: { name: error.name, message: error.message, stack: error.stack },
          severityLevel: SeverityLevel.Error
        },
        { ...properties }
      );
      observer.next(true);
      observer.complete();
    }).pipe(catchError(() => of(false)));
  }

  private logMetric(
    name: string,
    average?: number | null | undefined,
    properties?: { [key: string]: any } | null | undefined
  ) {
    return new Observable<boolean>(observer => {
      this.appInsights.trackMetric({ name: name, average: average ?? 0 }, properties ?? undefined);
      observer.next(true);
      observer.complete();
    }).pipe(catchError(() => of(false)));
  }

  private logPageView(name?: string, url?: string) {
    return new Observable<boolean>(observer => {
      this.appInsights.trackPageView({
        name,
        uri: url
      });
      observer.next(true);
      observer.complete();
    }).pipe(catchError(() => of(false)));
  }

  private logTrace(message: string, properties?: { [key: string]: any }) {
    return new Observable<boolean>(observer => {
      this.appInsights.trackTrace({ message }, properties);
      observer.next(true);
      observer.complete();
    }).pipe(catchError(() => of(false)));
  }
}
